import React, { forwardRef, useCallback, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import Popup from "../Popup/Popup";

import { getEventPath, isEventKey, KeyCode } from "../../utils/dom";
import { handleReactRef } from "../../utils/react";
import { EL_ID_PORTAL_MAIN_AFTER } from "../Portal/PortalMainAfter/constants";
import { useWhatInputIsLastPointerMouse } from "../../what-input/useWhatInput";
import useOutsideEvent from "@/shared/hooks/useOutsideEvent";
import { usePopup } from "../../hooks/usePopup";

import styles from "./dropDown.module.scss";

const DropDown = forwardRef(({
  isRenderBodyWhenSSR,
  hasConstantSizes,
  isUseLockBodyScroll,
  disabled,
  className = "",
  button,
  onClickButton,
  body,
  boundaryRect,
  hasBackdrop = false,
  toggleOnMouseOverLeave = false,
  closeOnPathnameChange,
  isPopupCoversButtonByDefault,
  isMaxWidthBasedOnButton,
  toggleOnClickButton,
  focusOnBecameVisible,
  onShow,
  onHide,
  popupProps,
  isMinWidthBasedOnButton,
  style,
  noPortal,
  ...restProps
}, ref) => {
  const [elRoot, setElRoot] = useState(null);
  const [elPopup, setElPopup] = useState(null);

  const { isShown, closePopup, openPopup, anchorEl } = usePopup(closeOnPathnameChange);
  const isLastInputMouse = useWhatInputIsLastPointerMouse();

  const handleClickButton = (e) => {
    e.stopPropagation();

    if (toggleOnClickButton && isShown) {
      hide();
    } else {
      show(e);

      if (onClickButton) {
        onClickButton(e);
      }
    }
  };

  const show = useCallback((e) => {
    openPopup(e);
    if (onShow) {
      onShow();
    }
  }, [openPopup, onShow]);

  const hide = useCallback(() => {
    closePopup();
    if (onHide) {
      onHide();
    }
  }, [closePopup, onHide]);

  const checkIfEventRelatedToThisFloatWindow = useCallback((e) => {
    const eventPath = getEventPath(e);

    const elPortalMainAfter = document.getElementById(EL_ID_PORTAL_MAIN_AFTER);

    if (!eventPath || !eventPath.includes(elPortalMainAfter)) {
      return true;
    }

    const elsChildrenPortalAfter = [...elPortalMainAfter.children];

    const indexThisRootInPortal = elsChildrenPortalAfter.findIndex((c) => {
      return c === elPopup;
    });

    if (indexThisRootInPortal === -1) {
      return true;
    }

    const indexInPortalEvent = elsChildrenPortalAfter.findIndex((c) => {
      return eventPath.includes(c);
    });

    if (indexInPortalEvent === -1) {
      return true;
    }

    return indexInPortalEvent < indexThisRootInPortal;
  }, [elPopup]);

  // FAST-FIX (need to investigate issue more, if troubles with the current fix):
  // Use "mouseup" for input mouse instead of "click".
  // There is a bug with click, on desktop:
  // 1) open small player TOC
  // 2) long press on item so the custom context menu appears
  // 3) release the mouse button
  // AR: TOC popup closes
  // ER: TOC popup is not closed
  const eventTypeOutsideEvent = isLastInputMouse ? "mouseup" : "touchstart";

  useOutsideEvent(
    [{current: elRoot}, elPopup], eventTypeOutsideEvent, hide, checkIfEventRelatedToThisFloatWindow,
    isShown,
  );

  const handleKeyDownRoot = (e) => {
    if (checkIfEventRelatedToThisFloatWindow(e)) {
      if (isEventKey(e, KeyCode.enter)) {
        if (!isShown) {
          show(e);
        }
      }
    }
  };

  const onHideFromProps = popupProps?.onHide;
  const handleHidePopup = useCallback((...args) => {
    hide();

    if (onHideFromProps) {
      onHideFromProps(...args);
    }
  }, [hide, onHideFromProps]);

  const handleRef = useCallback((el) => {
    if (ref) {
      const refData = {
        view: el,
        hide,
        show,
      };

      handleReactRef(ref, refData);
    }

    setElRoot(el);
  }, [ref, hide, show]);

  let anchorRectFinal;

  if (anchorEl && isPopupCoversButtonByDefault) {
    const rect = anchorEl.getBoundingClientRect();

    anchorRectFinal = {
      top: rect.top,
      left: rect.left,
      bottom: rect.top,
      right: rect.left,
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: 0,
    };
  }

  const contentPropsFinal = popupProps?.contentProps ? {...popupProps.contentProps} : {};

  const contentPropsStyleDefault = {};

  if (isMinWidthBasedOnButton) {
    contentPropsStyleDefault.minWidth = elRoot?.offsetWidth;
  }
  if (isMaxWidthBasedOnButton) {
    contentPropsStyleDefault.maxWidth = elRoot?.offsetWidth;
  }

  contentPropsFinal.style = popupProps?.contentProps?.style ? {
    ...popupProps.contentProps.style,
    ...contentPropsStyleDefault,
  } : contentPropsStyleDefault;

  const isCanToggleByMouseEnterLeave = toggleOnMouseOverLeave && isLastInputMouse;

  return (
    <div
      ref={handleRef}
      className={classNames(styles["base-drop-down"], className)}
      onMouseEnter={isCanToggleByMouseEnterLeave ? show : undefined}
      onMouseLeave={isCanToggleByMouseEnterLeave ? hide : undefined}
      onKeyDown={handleKeyDownRoot}
      style={style}
      {...restProps}
    >
      {typeof button === "function"
        ? button({
          show: isShown,
          hide,
          onClick: handleClickButton,
        })
        : button}
      <Popup
        ref={setElPopup}
        noPortal={noPortal}
        focusOnBecameVisible={focusOnBecameVisible}
        isUseLockBodyScroll={isUseLockBodyScroll}
        isShown={isShown}
        anchorEl={anchorEl}
        anchorRect={anchorRectFinal}
        boundaryRect={boundaryRect}
        hasBackdrop={hasBackdrop ?? !isCanToggleByMouseEnterLeave}

        {...popupProps}

        onHide={handleHidePopup}
        contentProps={contentPropsFinal}
        className={classNames(popupProps?.className, styles["dd-popup"])}
      >
        {(isShown || (isRenderBodyWhenSSR && !("window" in global))) && (
          typeof body === "function" ? body({ hide: hide }) : body
        )}
      </Popup>
    </div>
  );
});

DropDown.propTypes = {
  isRenderBodyWhenSSR: PropTypes.bool,
  isUseLockBodyScroll: PropTypes.bool,
  toggleOnMouseOverLeave: PropTypes.bool,
  className: PropTypes.string,
  boundaryRect: PropTypes.object,
  button: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onClick: PropTypes.func,
  body: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onShow: PropTypes.func,
  popupProps: PropTypes.object,
};

export default DropDown;
